import React, { useState, useCallback, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Home, FileText, Calendar, Settings,
  List, Inbox, Megaphone, MessageCircle, User,
  HelpCircle, Bolt, Receipt, ScrollText, Cable, Package,
  Bot, Users,
} from 'lucide-react';
import UserProfileDropdown from './UserProfileDropdown';
import BusinessSwitcherModal from './BusinessSwitcherModal';
import UserNotificationModal from './UserNotificationModal';
import logger from '../utils/logger';

// Define proper type for userdata that matches UserProfileDropdown requirements
type UserData = {
  firstName?: string;
  lastName?: string;
  organization?: {
    name: string;
  };
  countryCode?: string;
  phone?: string;
  bucketUrl?: string;
  profile?: {
    filePath?: string;
  };
  role?: {
    name: string;
  };
  userDetails?: {
    userPreference?: {
      screenPermission?: string[];
    };
  };
};

interface SidebarProps {
  userdata?: UserData;
}

// Static styles defined outside component - prevents recreation on each render
const sidebarStyle = {
  width: "200px", 
  overflowY: "auto" as const,
  overflowX: "hidden" as const,
  borderRight: "1px solid #e9ecef",
  backgroundColor: "#ffffff",
  position: "fixed" as const,
  top: 0,
  left: 0,
  bottom: 0,
  borderRadius: 0,
  margin: 0,
  zIndex: 1040,
  boxShadow: '1px 0 5px rgba(0,0,0,0.05)',
  display: 'flex',
  flexDirection: 'column' as const
};

const logoSectionStyle = {
  height: "40px", 
  minHeight: "40px",
  padding: "0 8px",
  position: "relative" as const,
  borderBottom: "1px solid #e9ecef",
  backgroundColor: "#ffffff",
  flexShrink: 0 as const
};

const dotBorderStyle = {
  position: "absolute" as const,
  bottom: 0,
  left: "20%",
  right: "20%",
  borderBottom: "2px dotted #e9ecef"
};

const logoStyle = {
  height: "20px",
  transition: "transform 0.2s ease"
};

const navListStyle = {
  listStyle: 'none' as const, 
  padding: "0.2rem 0.3rem",
  margin: 0,
  width: '100%',
  boxSizing: 'border-box' as const,
  flexGrow: 1,
  overflowY: 'auto' as const
};

const footerSectionStyle = {
  padding: "0.3rem", 
  flexShrink: 0 as const
};

const footerBoxStyle = {
  background: "#f8f9fa",
  border: "1px solid #e9ecef",
  borderRadius: "6px",
  padding: "0.2rem",
  marginBottom: "0.3rem"
};

const footerLinkBaseStyle = {
  fontSize: "0.85em",
  padding: "0.3rem 0.5rem",
  display: "flex",
  alignItems: "center",
  color: "#495057",
  borderRadius: "4px",
  transition: "background-color 0.2s ease",
};

const helpLinkStyle = {
  ...footerLinkBaseStyle,
  marginBottom: "0.2rem"
};

// NavLink items configuration with permission keys
const navItems = [
  { path: '/dashboard', Icon: LayoutDashboard, label: 'Dashboard', permissionKey: null },
  { path: '/leads', Icon: Home, label: 'Leads', permissionKey: null },
  { path: '/content', Icon: FileText, label: 'Content', permissionKey: null },
  { path: '/tasks', Icon: Calendar, label: 'Task', permissionKey: null },
  { path: '/invoicelist', Icon: Receipt, label: 'Invoices', permissionKey: null },
  { path: '/Quotationlist', Icon: ScrollText, label: 'Quotation', permissionKey: null },
  { path: '/products', Icon: Package, label: 'Products', permissionKey: null },
  { path: '/automation', Icon: Bolt, label: 'Automation', permissionKey: null },
  { path: '/form', Icon: List, label: 'Form', permissionKey: null },
  { path: '/whatsappInbox', Icon: Inbox, label: 'Inbox', permissionKey: null },
  { path: '/whatsappBroadcast', Icon: Megaphone, label: 'Campaigns', permissionKey: 'sidebar > hide > campaigns' },
  { path: '/template', Icon: MessageCircle, label: 'Template', permissionKey: 'sidebar > hide > templates' },
  { path: '/Auto-reply-bots', Icon: Bot, label: 'Auto-reply-bots', permissionKey: null },
  { path: '/profile', Icon: User, label: 'WABA Info', permissionKey: 'sidebar > hide > waba_info' },
  { path: '/integrations', Icon: Cable, label: 'integration', permissionKey: 'sidebar > hide > integrations' },
  // { path: '/customization', Icon: Settings, label: 'Customization', permissionKey: null }, // HIDDEN - Customization option removed
];

// Memoized NavItem component to prevent unnecessary renders
// Extracted outside component to prevent recreation
const getNavLinkStyle = (isActive: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '0.1rem 0.5rem',
  marginBottom: '0.1rem',
  borderRadius: '6px',
  color: isActive ? '#0d6efd' : '#495057',
  backgroundColor: isActive ? '#e7f1ff' : 'transparent',
  transition: 'all 0.2s ease',
  textDecoration: 'none',
  fontSize: '0.9rem'
});

const NavItem = React.memo(({ path, Icon, label }: { path: string; Icon: any; label: string }) => {
  return (
    <NavLink 
      className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
      style={({ isActive }) => getNavLinkStyle(isActive)}
      to={path}
    >
      <Icon size={18} opacity={0.5} />
      <span className="ms-2">{label}</span>
    </NavLink>
  );
});

// Memoized link hover handlers
const useHoverHandlers = () => {
  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.currentTarget.style.backgroundColor = "#edf2f7";
  }, []);

  const handleMouseLeave = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.currentTarget.style.backgroundColor = "transparent";
  }, []);

  return { handleMouseEnter, handleMouseLeave };
};

// Memoized Logo Section component to prevent re-renders
const LogoSection = React.memo(() => (
  <div className="d-flex justify-content-center align-items-center" style={logoSectionStyle}>
    <div style={dotBorderStyle} />
    <a href="/dashboard" className="d-flex align-items-center">
      <img 
        src="assets/img/3sigma_logo.png" 
        alt="3Sigma Logo" 
        style={logoStyle}
        className="hover:scale-105"
      />
    </a>
  </div>
));

// Memoized UserProfile component wrapper
const MemoizedUserProfileDropdown = React.memo(UserProfileDropdown);

// Memoized Modal to prevent re-renders
const MemoizedBusinessSwitcherModal = React.memo(BusinessSwitcherModal);

const Sidebar: React.FC<SidebarProps> = ({ userdata }) => {
  const [showSwitcherModal, setShowSwitcherModal] = useState(false);
  const { handleMouseEnter, handleMouseLeave } = useHoverHandlers();
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  // Get screen permissions from userdata - check multiple possible locations
  const screenPermissions = useMemo(() => {
    // Log the entire userdata structure for debugging
    logger.log('🔍 Sidebar userdata structure:', userdata);

    // Try multiple paths where screenPermission might exist
    const fromUserDetails = userdata?.userDetails?.userPreference?.screenPermission;
    const fromUserPreference = (userdata as any)?.userPreference?.screenPermission;
    const fromRoot = (userdata as any)?.screenPermission;

    const permissions = fromUserDetails || fromUserPreference || fromRoot || [];

    logger.log('✅ Screen permissions found:', permissions);

    return permissions;
  }, [userdata]);

  // Helper function to check if an item should be hidden
  const shouldHideItem = useCallback((permissionKey: string | null): boolean => {
    if (!permissionKey) return false; // No permission key means always show
    const isHidden = screenPermissions.includes(permissionKey);
    if (isHidden) {
      logger.log(`🚫 Hiding sidebar item with key: ${permissionKey}`);
    }
    return isHidden;
  }, [screenPermissions]);

  // Filter nav items based on permissions
  const filteredNavItems = useMemo(() => {
    const filtered = navItems.filter(item => !shouldHideItem(item.permissionKey));
    logger.log('📋 Total nav items:', navItems.length);
    logger.log('📋 Filtered nav items:', filtered.length);
    logger.log('📋 Visible items:', filtered.map(i => i.label));
    return filtered;
  }, [shouldHideItem]);

  // Memoized callback function for help website
  const openHelpWebsite = useCallback(() => {
    window.open("https://help.3sigmacrm.com", "_blank");
  }, []);

  // Memoized callback for what's new link
  const openWhatsNew = useCallback(() => {
    window.open("https://help.3sigmacrm.com/changelog/web-app", "_blank");
  }, []);

  // Memoized callback for business switcher with proper deps
  const handleSwitchBusiness = useCallback(() => {
    const dropdown = document.querySelector('.user-info-dropdown-toggler');
    (dropdown as any)?.click();
    setShowSwitcherModal(true);
  }, [setShowSwitcherModal]); // Add dependency

  // Memoized modal close handler
  const handleCloseModal = useCallback(() => {
    setShowSwitcherModal(false);
  }, [setShowSwitcherModal]);


  // 🆕 NOTIFICATION MODAL HANDLERS
const handleViewNotifications = useCallback(() => {
  logger.log('🔔 View notifications clicked - opening modal');
  setShowNotificationModal(true);
}, []);

const handleCloseNotificationModal = useCallback(() => {
  logger.log('🔔 Notification modal closed');
  setShowNotificationModal(false);
}, []);

  // Memoize the entire nav items section with filtered items
  const navItemsSection = useMemo(() => (
    <ul className="sidebar-nav" style={navListStyle}>
      <li>
        {filteredNavItems.map((item) => (
          <NavItem key={item.path} path={item.path} Icon={item.Icon} label={item.label} />
        ))}
      </li>
    </ul>
  ), [filteredNavItems]); // Depend on filteredNavItems

  // Memoize the footer section
  const footerSection = useMemo(() => (
    <div className="mt-auto" style={footerSectionStyle}>
      <div style={footerBoxStyle}>
        <NavLink
          className="nav-link get-help-link"
          to="#"
          onClick={openHelpWebsite}
          style={helpLinkStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <HelpCircle size={16} opacity={0.5} />
          <span className="ms-2">Get Help</span>
        </NavLink>
        
        <NavLink
          className="nav-link"
          to="#"
          onClick={openWhatsNew}
          style={footerLinkBaseStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Bolt size={16} opacity={0.5} />
          <span className="ms-2">What's New</span>
        </NavLink>
      </div>
    </div>
  ), [openHelpWebsite, openWhatsNew, handleMouseEnter, handleMouseLeave]);

  // Memoize the entire sidebar content to prevent re-renders
  const sidebarContent = useMemo(() => (
    <aside id="sidebar" className="sidebar d-flex flex-column" style={sidebarStyle}>
      {/* Logo Section - Memoized component */}
      <LogoSection />

      {/* Navigation Section - Memoized */}
      {navItemsSection}

      {/* Footer Section - Memoized */}
      {footerSection}
      
      {/* User Profile Section - Memoized component */}
     <MemoizedUserProfileDropdown 
  userdata={userdata} 
  onSwitchBusiness={handleSwitchBusiness}
  onViewNotifications={handleViewNotifications} // 🆕 ADD THIS LINE
/>
    </aside>
  ), [userdata, navItemsSection, footerSection, handleSwitchBusiness,handleViewNotifications]);

  return (
    <>
      {sidebarContent}
      <MemoizedBusinessSwitcherModal 
        isOpen={showSwitcherModal}
        onClose={handleCloseModal}
      />
    {/* 🆕 USER NOTIFICATION MODAL */}
<UserNotificationModal
  isOpen={showNotificationModal}
  onClose={handleCloseNotificationModal}
/>
    </>
  );
};

// Wrap the entire component in React.memo to prevent unnecessary rerenders
export default React.memo(Sidebar);